import { createReducer } from 'typesafe-actions'
import {
  GetClientTypeDetailsResponseDto,
  GetListOfClientTypesResponseDto,
  ReadClientTypesForAssignToUserResponseDto,
  ReadClientTypesRequestDto,
  ReadClientTypesResponseDto,
} from '@sparling/financial-common'
import { omit } from 'lodash'
import { ActionTypes } from '../types'
import { LoadingStateEnum } from '../../types'
import {
  getClientTypesForAssignToUserRequest,
  getClientTypesForAssignToUserSuccess,
  getClientTypesForAssignToUserFailure,
  getClientTypesRequest,
  getClientTypesSuccess,
  getClientTypesFailure,
  getClientTypeDetailsRequest,
  getClientTypeDetailsSuccess,
  getClientTypeDetailsFailure,
  openCreateClientTypeModal,
  openUpdateClientTypeModal,
  openViewClientTypeModal,
  getListOfClientTypesRequest,
  getListOfClientTypesSuccess,
  getListOfClientTypesFailure,
} from '../actions'

export type ClientTypesState = {
  listOfClientTypes: ListOfClientTypes
  clientTypesForAssignToUser: ClientTypesForAssignToUser
  clientTypeDetails: ClientTypeDetails
  createClientTypeModal: CreateModalData
  updateClientTypeModal: UpdateModalData
  viewClientTypeModal: ViewModalData
  loading: LoadingStateEnum
  data: ReadClientTypesResponseDto
  error: Error | null
  filters: ReadClientTypesRequestDto
}

type ListOfClientTypes = {
  loading: LoadingStateEnum
  data: GetListOfClientTypesResponseDto[]
  error: Error | null
}

type ClientTypesForAssignToUser = {
  loading: LoadingStateEnum
  data: ReadClientTypesForAssignToUserResponseDto
  error: Error | null
}

type ClientTypeDetails = {
  loading: LoadingStateEnum
  data: GetClientTypeDetailsResponseDto | null
  error: Error | null
}

type ModalState = {
  open: boolean
}

type CreateModalData = ModalState
type UpdateModalData = ModalState & { id: string | null; currentName: string | null }
type ViewModalData = ModalState

const initialMeta = {
  itemCount: 0,
  totalItems: 0,
  itemsPerPage: 0,
  totalPages: 0,
  currentPage: 0,
}

export const initialState = {
  listOfClientTypes: {
    loading: LoadingStateEnum.IDLE,
    data: [],
    error: null,
  },
  clientTypesForAssignToUser: {
    loading: LoadingStateEnum.IDLE,
    data: {
      items: [],
      meta: initialMeta,
    },
    error: null,
  },
  clientTypeDetails: {
    loading: LoadingStateEnum.IDLE,
    data: null,
    error: null,
  },
  createClientTypeModal: { open: false },
  updateClientTypeModal: { open: false, id: null, currentName: null },
  viewClientTypeModal: { open: false },
  loading: LoadingStateEnum.IDLE,
  data: {
    items: [],
    meta: initialMeta,
  },
  error: null,
  filters: {},
}

export const clientTypes = createReducer<ClientTypesState, ActionTypes>(initialState)
  .handleAction(getListOfClientTypesRequest, (state) => ({
    ...state,
    listOfClientTypes: {
      ...state.listOfClientTypes,
      loading: LoadingStateEnum.LOADING,
    },
  }))
  .handleAction(getListOfClientTypesSuccess, (state, { payload }) => ({
    ...state,
    listOfClientTypes: {
      ...state.listOfClientTypes,
      loading: LoadingStateEnum.SUCCESS,
      data: payload,
    },
  }))
  .handleAction(getListOfClientTypesFailure, (state, { payload }) => ({
    ...state,
    listOfClientTypes: {
      ...state.listOfClientTypes,
      loading: LoadingStateEnum.ERROR,
      error: payload,
    },
  }))
  .handleAction(getClientTypesForAssignToUserRequest, (state) => ({
    ...state,
    clientTypesForAssignToUser: {
      ...state.clientTypesForAssignToUser,
      loading: LoadingStateEnum.LOADING,
    },
  }))
  .handleAction(getClientTypesForAssignToUserSuccess, (state, { payload }) => ({
    ...state,
    clientTypesForAssignToUser: {
      ...state.clientTypesForAssignToUser,
      loading: LoadingStateEnum.SUCCESS,
      data: payload,
    },
  }))
  .handleAction(getClientTypesForAssignToUserFailure, (state, { payload }) => ({
    ...state,
    clientTypesForAssignToUser: {
      ...state.clientTypesForAssignToUser,
      loading: LoadingStateEnum.ERROR,
      error: payload,
    },
  }))
  .handleAction(getClientTypesRequest, (state, { payload }) => ({
    ...state,
    filters: {
      ...state.filters,
      ...payload,
      page: Object.keys(omit(payload, 'page')).length > 0 ? 1 : payload.page,
    },
    loading: LoadingStateEnum.LOADING,
  }))
  .handleAction(getClientTypesSuccess, (state, { payload }) => ({
    ...state,
    loading: LoadingStateEnum.SUCCESS,
    data: payload,
  }))
  .handleAction(getClientTypesFailure, (state, { payload }) => ({
    ...state,
    loading: LoadingStateEnum.ERROR,
    error: payload,
  }))
  .handleAction(getClientTypeDetailsRequest, (state) => ({
    ...state,
    clientTypeDetails: {
      ...state.clientTypeDetails,
      loading: LoadingStateEnum.LOADING,
    },
  }))
  .handleAction(getClientTypeDetailsSuccess, (state, { payload }) => ({
    ...state,
    clientTypeDetails: {
      ...state.clientTypeDetails,
      loading: LoadingStateEnum.SUCCESS,
      data: payload,
    },
  }))
  .handleAction(getClientTypeDetailsFailure, (state, { payload }) => ({
    ...state,
    clientTypeDetails: {
      ...state.clientTypeDetails,
      loading: LoadingStateEnum.ERROR,
      error: payload,
    },
  }))
  .handleAction(openCreateClientTypeModal, (state, { payload: { open } }) => ({
    ...state,
    createClientTypeModal: { open },
  }))
  .handleAction(openUpdateClientTypeModal, (state, { payload: { open, id, currentName } }) => ({
    ...state,
    updateClientTypeModal: { open, id, currentName },
  }))
  .handleAction(openViewClientTypeModal, (state, { payload: { open } }) => ({
    ...state,
    viewClientTypeModal: { open },
    ...(!open && { clientTypeDetails: { loading: LoadingStateEnum.IDLE, data: null, error: null } }),
  }))
